import {
  AFTEventLogging,
  AladdinFrontendTelemetry,
  AladdinTelemetryConfigBuilder,
} from '@blk/aladdin-frontend-telemetry';
import { AWEEvent } from '@blk/aladdin-graph-everything/platform/event_logging/awe_event/v1/awe_event_pb';
import { agraph_platform_event_logging_awe_event_v1_AWEEventSchema as EventSchema } from '@blk/aladdin-graph-everything/platform/event_logging/awe_event/v1/awe_event_pb_schema';
import { getUrlParam } from '@blk/aw-api-clients';
import { isEmpty } from 'lodash';
import { BaseLogger } from '../BaseLogger';
import { Logger } from '../Logger';
import { LogAction, SESSION_ID_STORAGE_KEY } from '../constants';
import { AppMetadata, EventType, TrackingData } from '../models';
import { LoggerUtil, loggingLocalStorage, loggingLocalStorageEnums } from '../utils';

type ConnectionType = {
  downlink?: number;
  downlinkMax?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
  type?: string;
};
type PerformanceMemory = {
  jsHeapSizeLimit?: number;
  totalJSHeapSize?: number;
  usedJSHeapSize?: number;
};

interface NavigatorType extends Navigator {
  connection?: ConnectionType;
}

interface PerformanceType extends Performance {
  memory?: PerformanceMemory;
}
/*
 * Copied from @blk/aw-utils to prevent circular dependency
 * http://www.ietf.org/rfc/rfc4122.txt
 */
export function UUID(): string {
  // if secure context is available
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // otherwise, fall back to pseudo-random number generation. UUID is not used for security purposes in the frontend.
  const hexDigits = '0123456789abcdef'; // Hexadecimal digits
  const s: string[] = Array(36).fill(''); // Array to hold the UUID characters

  for (let i = 0; i < 36; i++) {
    // Insert hyphens at specific positions
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      s[i] = '-';
    }
    // Set the version number to '4' at position 14
    else if (i === 14) {
      s[i] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
    }
    // Generate random values for other positions
    else {
      const randomValue = Math.floor(Math.random() * 16);
      s[i] = hexDigits[i === 19 ? (randomValue & 0x3) | 0x8 : randomValue]; // bits 6-7 of the clock_seq_hi_and_reserved to 01
    }
  }

  return s.join(''); // Join the array into a string and return the UUID
}

/**
 * Checks if the given value has file descriptors
 * @param val - The value to check
 * @returns True if the value has file descriptors, false otherwise
 */
function hasFileDescriptors(val: unknown): val is { file: Array<Record<string, unknown>> } {
  if (typeof val !== 'object' || val === null) {
    return false;
  }
  // val is a non-null object: safe to narrow for property inspection
  const obj = val as Record<string, unknown>;
  return 'file' in val && Array.isArray(obj.file);
}

/**
 * Strips source_code_info from the given schema
 * @param rawSchema - The schema to strip
 * @returns The stripped schema
 */
function stripSourceCodeInfoFromSchema(rawSchema: string): string {
  try {
    const parsed: unknown = JSON.parse(rawSchema);
    if (!hasFileDescriptors(parsed)) {
      return rawSchema;
    }
    const strippedFiles = parsed.file.map((fd) =>
      Object.fromEntries(Object.entries(fd).filter(([key]) => key !== 'source_code_info'))
    );
    return JSON.stringify({ ...parsed, file: strippedFiles });
  } catch {
    return rawSchema;
  }
}

/*
 * Wraps EventSchema to strip source_code_info (proto compiler line maps/comments) from the
 * FileDescriptorSet JSON sent on every statcollector request. source_code_info is tooling
 * metadata and is not used by the StatCollector for event parsing. Stripping it reduces the
 * gzip-compressed payload from ~79KB to ~15KB, resolving the 64KB keepalive size limit.
 * The result is cached since EventSchema is a module-level constant.
 */
const _strippedEventSchema = (() => {
  let cached: string | null = null;
  return {
    ...EventSchema,
    getJSONSchemaAsString: (): string => {
      if (cached === null) {
        cached = stripSourceCodeInfoFromSchema(EventSchema.getJSONSchemaAsString());
      }
      return cached;
    },
  };
})();

/**
 * Represents the AFTLogger class.
 * @implements BaseLogger
 */
export class AFTLogger implements BaseLogger {
  private static appMetadata: AppMetadata;
  private static blackListedEvents: string[] = [];
  private static config: AFTEventLogging;
  private static payloadMappings: { [key: string]: string };
  private static _eventQueue: TrackingData[] = [];
  // Queue to store http request logs
  private static _httpReqLogsQueue: TrackingData[] = [];
  // Timestamp of last flush to prevent double-flush from simultaneous beforeunload + visibilitychange
  private static _lastFlushTime: number | null = null;
  /**
   * @param appMetadata
   * @description This method is used to initialize the logger
   * @example AFTLogger.initLogger({name: 'aw-advisor', version: '1.0.0', client: 'DEV', queSize: 10, isDefaultConfig: true});
   */
  async initLogger(appMetadata: AppMetadata): Promise<void> {
    AFTLogger.appMetadata = appMetadata;
    AFTLogger._lastFlushTime = null;
    try {
      const config = await new AladdinTelemetryConfigBuilder()
        .setAppName(AFTLogger.appMetadata.name ?? '')
        .setAppVersion(AFTLogger.appMetadata.version ?? '1.0.0')
        .setClientName(AFTLogger.appMetadata.client ?? '')
        .disableUserName(AFTLogger.appMetadata.disableUserName ?? false)
        .setQueueSize(AFTLogger.appMetadata.queueSize ?? 10)
        .markAsDefault(AFTLogger.appMetadata.isDefaultConfig ?? false)
        .setSessionIdCallback(AFTLogger.appMetadata.setSessionIdFn ?? AFTLogger.setSessionId)
        .setUserName(AFTLogger.appMetadata.userId ?? '')
        .build();

      if (!AFTLogger.config) {
        // setup intervals and event listeners only once
        this.initializeListeners();
      }
      /*
       * unregister the existing config to avoid duplicate logger instances
       * post existing events added to the config queue if any before un-registering it
       */
      AFTLogger.config?.post();
      AFTLogger.config?.unregister();
      AFTLogger.config = config;
      /*
       * Check if session is partner originated and set the partner source in app metadata
       * Validate against allowlist to prevent data injection attacks (CWE-918)
       */
      const partnerSource = getUrlParam('partner_source');
      if (partnerSource && /^[a-zA-Z0-9_-]+$/.test(partnerSource)) {
        AFTLogger.appMetadata.partnerSource = partnerSource;
      }
    } catch (error) {
      console.error('Error while initializing AFTLogger', error);
    }
  }
  /**
   * @description This method is used to track events
   * @example AFTLogger.trackEvent({section: 'section', element: 'element', action: 1, event: 'event'});
   */
  trackEvent(
    trackingData: TrackingData,
    config: AFTEventLogging = AFTLogger.config,
    functionName: string = 'track-event'
  ): void {
    if (!AFTLogger.blackListedEvents.includes(trackingData.action)) {
      // set the partner source on an event if available
      LoggerUtil.setPartnerSource(trackingData, AFTLogger.appMetadata);
      // set web entity id if enabled
      LoggerUtil.setWebEntityAndTrackingId(trackingData, AFTLogger.appMetadata);
      // Log http request events only to bff logger with special batch size of 30
      if (trackingData.action === LogAction.HTTP_REQUEST) {
        trackingData.queueTimestamp = new Date().toISOString(); // Capture queue insertion time for Splunk event ordering
        AFTLogger._httpReqLogsQueue.push(trackingData);
        if (AFTLogger._httpReqLogsQueue.length >= 30) {
          // send events to splunk
          AFTLogger.postToSplunk(AFTLogger._httpReqLogsQueue);
        }
        return;
      }
      const aft = AladdinFrontendTelemetry.getInstance();
      if (aft) {
        /*
         * Setting the tracking data which follows custom schema
         * wiki: https://webster.bfm.com/Wiki/display/AFWM/AW+Event+Logging+%28Web%29+-+Developer+Guide#AWEventLogging(Web)DeveloperGuide-Eventloggingschema
         */
        const event = new AWEEvent();
        if (event) {
          LoggerUtil.setAppInfo(event, trackingData, AFTLogger.appMetadata);
          LoggerUtil.setDomainSpecificInfo(event, trackingData);
          LoggerUtil.setElementInfo(event, trackingData);
          LoggerUtil.setErrorInfo(event, trackingData);
          LoggerUtil.setFeatureInfo(event, trackingData, AFTLogger.appMetadata);
          LoggerUtil.setSessionInfo(event, AFTLogger.appMetadata, config?.sessionID);
          LoggerUtil.setUserInfo(event, AFTLogger.appMetadata);

          if (config) {
            if (isEmpty(config.userName)) {
              config.userName = AFTLogger.appMetadata?.userId ?? '';
            }
            if (window.location.hostname === 'localhost' || LoggerUtil.isDebugModeEnabled()) {
              /**
               * Users can exclude events from local logging via the admin panel. The excluded events/tags
               * can be accessed from localStorage. The events will still be pushed to splunk, we just skip the
               * console.debug
               */
              const userExcludedTags: string[] =
                loggingLocalStorage.get(loggingLocalStorageEnums.excludeEventTags) ?? [];
              const userExcludedEvents: string[] =
                loggingLocalStorage.get(loggingLocalStorageEnums.excludeEventNames) ?? [];

              const eventName = event.getElement()?.getEvent();
              const rawMetadata = event.getElement()?.getMetadata();
              const eventTag = LoggerUtil.getTagFromEventMetadata(
                JSON.parse(!rawMetadata || rawMetadata === '' ? '{}' : rawMetadata)
              );

              if (
                !(eventName && userExcludedEvents.includes(eventName)) &&
                !(eventTag && userExcludedTags.includes(eventTag))
              ) {
                // eslint-disable-next-line no-console -- this is needed for debugging/testing
                console.debug('Tracked Event:', {
                  ...event.toObject(),
                  element: {
                    ...event.getElement()?.toObject(),
                    eventType:
                      Object.keys(EventType)[Object.values(EventType).indexOf(event.getElement()?.getEventType())],
                  },
                });
              }
            }
            trackingData.queueTimestamp = new Date().toISOString(); // Capture queue insertion time for Splunk event ordering
            AFTLogger._eventQueue.push(trackingData);
            if (AFTLogger._eventQueue.length >= config.queueSize) {
              // send events to splunk
              AFTLogger.postToSplunk(AFTLogger._eventQueue, config);
            }
            config.add(event, _strippedEventSchema, functionName);
          }
        }
      }
    }
  }

  // reset session id
  resetSessionId(): void {
    sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);
    AFTLogger.config.sessionID = AFTLogger.config.sessionIdCreator();
  }

  // set app metadata
  setAppMetadata(appMetadata: AppMetadata): void {
    AFTLogger.appMetadata = { ...AFTLogger.appMetadata, ...appMetadata };
  }

  public static setPayloadMappings(payloadMappings: { [key: string]: string }): void {
    AFTLogger.payloadMappings = { ...AFTLogger.payloadMappings, ...payloadMappings };
  }

  public static getPayloadMappings(): { [key: string]: string } {
    return AFTLogger.payloadMappings;
  }

  public static setBlackListedEvents(blackListedEvents: string[]): void {
    AFTLogger.blackListedEvents = [...AFTLogger.blackListedEvents, ...blackListedEvents];
  }

  public static getBlackListedEvents(): string[] {
    return AFTLogger.blackListedEvents;
  }

  private static setSessionId(): string {
    let sessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (!sessionId) {
      sessionId = UUID();
      sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
    }
    return sessionId;
  }

  private static postToSplunk(
    queue: TrackingData[] = AFTLogger._eventQueue,
    config: AFTEventLogging = AFTLogger.config,
    keepalive = false
  ): void {
    if (queue.length) {
      const queueCopy = [...queue, this.trackBrowserInfo()];
      queue.length = 0;
      LoggerUtil.postEventLog(queueCopy, AFTLogger.appMetadata, config?.sessionID, config?.clientName, keepalive);
    }
  }

  /**
   * Flush all queued events immediately with keepalive enabled.
   * Can be called manually before programmatic redirects/navigations,
   * or automatically via visibilitychange listener.
   * Debounced to 500ms to prevent double-flush when programmatic flush()
   * is followed immediately by visibilitychange (e.g., location.replace triggers both).
   */
  flush(): void {
    const now = Date.now();
    if (AFTLogger._lastFlushTime !== null && now - AFTLogger._lastFlushTime < 500) {
      return;
    }
    AFTLogger._lastFlushTime = now;
    AFTLogger.config?.post(true);
    // send events to splunk - drain each queue individually with keepalive=true
    AFTLogger.postToSplunk(AFTLogger._eventQueue, AFTLogger.config, true);
    AFTLogger.postToSplunk(AFTLogger._httpReqLogsQueue, AFTLogger.config, true);
  }

  /**
   * This method is used to initialize the listeners
   */
  initializeListeners(): void {
    // setup event listeners for default tracking
    document.addEventListener('click', Logger.declarativeTrackingHandler);
    // track paste events containing sensitive data
    document.addEventListener('paste', (event: ClipboardEvent) => {
      const trackingData = LoggerUtil.handleSensitiveDataPaste(event);
      if (trackingData) {
        this.trackEvent(trackingData);
      }
    });
    // post events by polling
    setInterval(() => {
      AFTLogger.config?.post();
      // send event to splunk
      AFTLogger.postToSplunk();
    }, AFTLogger.appMetadata.postInterval ?? 10000);
    // Interval to post http request events after every 30 seconds
    setInterval(() => {
      // send events to splunk
      AFTLogger.postToSplunk(AFTLogger._httpReqLogsQueue);
    }, 30000);
    /*
     * beforeunload covers programmatic navigations (location.replace, location.href, reload)
     * where visibilitychange may not fire before page teardown.
     * The AFT library registers its own beforeunload listener that calls config.post(true),
     * so we only need to drain our Splunk queues here to avoid a redundant double-post.
     */
    window.addEventListener('beforeunload', () => {
      AFTLogger.postToSplunk(AFTLogger._eventQueue, AFTLogger.config, true);
      AFTLogger.postToSplunk(AFTLogger._httpReqLogsQueue, AFTLogger.config, true);
    });
    // visibilitychange covers tab switch, minimize, mobile background
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  /**
   * Tracks the browser information such as network info, memory info, and timezone.
   */
  private static trackBrowserInfo(): TrackingData {
    const navigatorRef: NavigatorType = navigator;
    const networkInfo: ConnectionType | null = !!navigatorRef.connection
      ? {
          downlink: navigatorRef.connection.downlink,
          effectiveType: navigatorRef.connection.effectiveType,
          saveData: navigatorRef.connection.saveData,
        }
      : null;
    const performanceRef: PerformanceType = performance;
    const memoryInfo: PerformanceMemory | null = !!performanceRef.memory
      ? {
          jsHeapSizeLimit: performanceRef.memory.jsHeapSizeLimit,
          totalJSHeapSize: performanceRef.memory.totalJSHeapSize,
          usedJSHeapSize: performanceRef.memory.usedJSHeapSize,
        }
      : null;
    return {
      action: LogAction.BROWSER_INFO,
      elementInfo: {
        metadata: JSON.stringify({
          ...(networkInfo ? { networkInfo } : {}),
          ...(memoryInfo ? { memoryInfo } : {}),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
        eventType: EventType.EVENT_LOAD,
      },
      queueTimestamp: new Date().toISOString(), // Capture queue insertion time for Splunk event ordering
    };
  }
}
